"use client";

import { ChatSkeleton } from "@/components/ui/skeleton";
import { AgentDialog, type AgentDialogSelection } from "@/components/agent-dialog";
import { AppSidebar, type SidebarItem } from "@/components/app-sidebar";
import { ChatView, type MyFilesScopeConfig } from "@/components/chat-view";
import { FilesPanel } from "@/components/files-panel";
import { MemoriesPanel } from "@/components/memories-panel";
import { ProfilePanel } from "@/components/profile-panel";
import { SystemPanel } from "@/components/system-panel";
import { ToolsBrowseDialog } from "@/components/tools-browse-dialog";
import { Button } from "@/components/ui/button";
import { SignInScreen } from "@/components/sign-in-screen";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { MenuIcon } from "@/components/ui/menu";
import { useAgents } from "@/hooks/use-agents";
import { useArtifacts } from "@/hooks/use-artifacts";
import { useChats } from "@/hooks/use-chats";
import { isValidModelId, type ModelId } from "@/hooks/use-model";
import { profileToolsToRecord } from "@/hooks/use-user-settings";
import { useTools } from "@/hooks/use-tools";
import { useSession } from "@/hooks/use-session";
import { useTeams } from "@/hooks/use-teams";
import { SIDEBAR_ROUTES } from "@/lib/sidebar-routes";
import { BUILTIN_TOOL_UUID } from "@/lib/builtin-tools";
import { readSafeReturnTo } from "@/lib/return-navigation";
import { supabase } from "@/lib/supabase";
import { useTabTitle } from "@/components/tab-title-provider";
import type { DbAgent, DbChatType, DbModelTier } from "@/types/db";
import type { FileUIPart } from "ai";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminChatsPanel } from "../components/admin-chats-panel";
import { MyFeedbackPanel } from "@/components/my-feedback-panel";
import { ArchivedProjectsPanel } from "../components/archived-projects-panel";
import { ArchivedAgentsPanel } from "../components/archived-agents-panel";
import { AnalyticsPanel } from "../components/analytics-panel";
import { AgentAnalyticsPanel } from "../components/agent-analytics-panel";
import { DocumentationPanel } from "../components/documentation-panel";
import { PlaygroundPanel } from "../components/playground-panel";
import { PolicyPanel } from "../components/policy-panel";
import { UsersPanel } from "../components/users-panel";
import { ParseJobsPanel } from "../components/parse-jobs-panel";
import { MailCommsPanel } from "../components/mail-comms-panel";
import { EvaluationsPanel } from "@/components/evaluations-panel";
import { TokenAnalyticsPanel } from "../components/token-analytics-panel";
import { ModelAnalyticsPanel } from "../components/model-analytics-panel";

type HomeProfile = {
  role: string | null;
  model_tier: DbModelTier | null;
  display_name: string | null;
  username: string | null;
};

const EMPTY_PROFILE: HomeProfile = {
  role: null,
  model_tier: null,
  display_name: null,
  username: null,
};

// Never a real user id — used as chatOwnerId while the project chat owner
// fetch is in flight, so ChatView's `!chatOwnerId` fallback can't be misread
// as "unowned, treat viewer as owner" and briefly show the send box to a
// non-owner.
const PROJECT_CHAT_OWNER_LOADING = "__loading__";

const HOME_PANEL_TITLES: Partial<Record<SidebarItem, string>> = {
  analytics: "Global Analytics",
  "agent-analytics": "Agents Analytics",
  "archived-projects": "Archived Projects",
  "archived-agents": "Archived Agents",
  chats: "Feedback Chats",
  documentation: "Documentation",
  files: "Library",
  "mail-comms": "Mail Comms",
  evaluations: "Evaluations",
  memories: "Memories",
  "model-analytics": "Model Analytics",
  "my-feedback": "My Feedback",
  "parse-jobs": "Large File",
  playground: "Playground",
  policy: "AI Policy",
  system: "System",
  "token-analytics": "Token Analytics",
  users: "Users",
};

const getDisplayName = (
  profile: Pick<HomeProfile, "display_name" | "username">,
  email?: string | null
) => {
  const displayName = profile.display_name?.trim();

  if (displayName) {
    return displayName;
  }

  const username = profile.username?.trim();

  if (username) {
    return username;
  }

  const emailLocalPart = email?.split("@")[0]?.trim();

  return emailLocalPart || "User";
};

const getFirstName = (name: string) => name.trim().split(/\s+/)[0] || "User";

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2) || "U";

type HomeClientProps = {
  workflowsAllowedModelTiers: DbModelTier[] | null;
  codeSpacesAllowedModelTiers: DbModelTier[] | null;
};

function HomeContent({ workflowsAllowedModelTiers, codeSpacesAllowedModelTiers }: HomeClientProps) {
  const session = useSession();
  const sessionUserId = session?.user?.id ?? null;
  const [activeNav, setActiveNav] = useState<SidebarItem>("new-chat");
  const [pendingPrompt, setPendingPrompt] = useState<{
    key: number;
    content: string;
    name: string;
    autoSend?: boolean;
    files?: FileUIPart[];
  } | null>(null);
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);
  const [pendingLiveChatStart, setPendingLiveChatStart] = useState(false);
  const [pendingModelOverride, setPendingModelOverride] = useState<ModelId | null>(null);
  // One-shot composer toggle overrides (?ws=/?ce= from the project page) applied
  // to the next new chat via ChatView's initialToolOverrides, so execution honors
  // the toggles the project composer showed (agent-derived or user defaults).
  const [pendingToolOverrides, setPendingToolOverrides] = useState<Partial<
    Record<string, boolean>
  > | null>(null);
  // One-shot myFiles scope (?kbFileIds=/?kbCollectionIds= from the Knowledge Base
  // page's "Start chat" action) applied to the next new chat via ChatView's
  // initialMyFilesConfig, scoping the myFiles tool to just those files/collections.
  const [pendingMyFilesConfig, setPendingMyFilesConfig] = useState<MyFilesScopeConfig | null>(null);
  // Short human-readable label for the myFiles scope above (e.g. a file name,
  // "3 files", or a collection name) — shown as a pill in ChatView so the
  // chat visibly reflects it was started from the Knowledge Base page.
  const [pendingMyFilesLabel, setPendingMyFilesLabel] = useState<string | null>(null);
  const [incognitoMode, setIncognitoMode] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // null = temp new chat (not yet saved), string = persisted chat UUID
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  // Mirror of activeChatId readable inside the ?chat deep-link effect without
  // adding it as a dependency (the effect must stay keyed on the URL only).
  const activeChatIdRef = useRef<string | null>(null);
  activeChatIdRef.current = activeChatId;
  // Active project_chats record (mutually exclusive with activeChatId).
  const [activeProjectChatId, setActiveProjectChatId] = useState<string | null>(null);
  const [activeProjectChatName, setActiveProjectChatName] = useState<string | null>(null);
  // Persisted model of the active project chat, fetched on open so the per-chat
  // model override survives reloads (project chats aren't in the `chats` list).
  const [activeProjectChatModel, setActiveProjectChatModel] = useState<string | null>(null);
  // Owner of the active project chat, fetched directly from the DB (not from
  // URL params) so read-only enforcement can't be bypassed or lost on
  // refresh/bookmark. null while loading — treated as "not yet known", never
  // as "owned by me", to avoid a flash of the send box for a shared chat.
  const [activeProjectChatOwnerId, setActiveProjectChatOwnerId] = useState<string | null>(null);
  // Persisted agent of the active project chat, fetched on open — project
  // chats aren't in the `chats` list, so unlike personal chats this can't be
  // read off `activeChat.agent_id`. Without it, reopening an existing project
  // chat would silently lose the agent's model/prompt/tools/knowledge base
  // for every turn (falling back to plain project-chat defaults).
  const [activeProjectChatAgentId, setActiveProjectChatAgentId] = useState<string | null>(null);
  // Mirror readable inside the ?projectChat deep-link effect without adding it
  // as a dependency (the effect must stay keyed on the URL only).
  const activeProjectChatIdRef = useRef<string | null>(null);
  activeProjectChatIdRef.current = activeProjectChatId;

  // App-shell-level "response ready" notification (for a turn finishing on
  // a chat/page other than the one on screen) now lives in
  // global-notifications.tsx, mounted from the root layout instead of here
  // — this component only covers "/", so navigating to /chats, /projects,
  // etc. used to unmount it entirely, silently killing the notification
  // for exactly the case it exists to cover. See that file's doc comment.

  // Incremented only on explicit navigation (new chat / select chat).
  // Keeps ChatView mounted when the DB record is created mid-conversation.
  const [chatSessionKey, setChatSessionKey] = useState(0);
  // True for freshly started sessions (new chat, agent activation).
  // False when loading an existing chat from the sidebar.
  const [isNewChatSession, setIsNewChatSession] = useState(true);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [documentationDocId, setDocumentationDocId] = useState<string | null>(null);
  const [profile, setProfile] = useState<HomeProfile>(EMPTY_PROFILE);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { chats, refresh, createChat, createProjectChat, updateChatName, updateChatModel, updateChatAgent, applyChatAgentDeactivation, updateProjectChatModel, updateProjectChatName, deleteChat, deleteProjectChat } = useChats();
  const { agents, loading: agentsLoading } = useAgents();
  const { tools } = useTools();
  const { teams, personalTeamId, loading: teamsLoading } = useTeams();
  const { createArtifact } = useArtifacts();
  const resolvedUserDisplayName = useMemo(
    () => getDisplayName(profile, session?.user?.email),
    [profile, session?.user?.email]
  );
  const userDisplayName = useMemo(
    () => (isProfileLoaded ? resolvedUserDisplayName : ""),
    [isProfileLoaded, resolvedUserDisplayName]
  );
  const userFirstName = useMemo(() => getFirstName(userDisplayName), [userDisplayName]);
  const userInitials = useMemo(() => getInitials(userDisplayName), [userDisplayName]);
  const userRole = profile.role;
  const userModelTier = profile.model_tier ?? "standard";

  // Compute workflows visibility from server-evaluated flag + loaded user model tier.
  const workflowsEnabled = useMemo(
    () => isProfileLoaded && (workflowsAllowedModelTiers?.includes(userModelTier) ?? false),
    [isProfileLoaded, workflowsAllowedModelTiers, userModelTier]
  );

  const codeSpacesEnabled = useMemo(
    () => isProfileLoaded && (codeSpacesAllowedModelTiers?.includes(userModelTier) ?? false),
    [isProfileLoaded, codeSpacesAllowedModelTiers, userModelTier]
  );

  useEffect(() => {
    if (!session) {
      setProfile(EMPTY_PROFILE);
      setIsProfileLoaded(false);
      return;
    }

    let cancelled = false;
    setIsProfileLoaded(false);

    const loadProfile = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("role, model_tier, display_name, username, has_onboarded")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (cancelled) {
        return;
      }

      if (error) {
        console.error("[Home] failed to load profile:", error.message ?? error);
        setProfile(EMPTY_PROFILE);
        setIsProfileLoaded(true);
        return;
      }

      if (!data?.has_onboarded) {
        router.replace("/onboarding");
        return;
      }

      setProfile({
        role: data?.role ?? null,
        model_tier: data?.model_tier ?? null,
        display_name: data?.display_name ?? null,
        username: data?.username ?? null,
      });
      setIsProfileLoaded(true);
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [session, router]);

  // Deep-link: ?chat=<id> → activate that chat immediately without waiting
  // for the chats list to load. This avoids the WelcomeScreen flash when
  // navigating from the projects page with a pre-filled prompt.
  // ?chatOwner=<userId> is set when navigating to a foreign shared chat so
  // ChatView can derive read-only mode without an extra DB fetch.
  const [deepLinkChatOwnerId, setDeepLinkChatOwnerId] = useState<string | null>(
    () => searchParams.get("chatOwner")
  );
  const deepLinkChatId = searchParams.get("chat");
  useEffect(() => {
    if (!deepLinkChatId) return;
    // The chat is already the active one (e.g. the chats list page just
    // navigated here, or handleChatCreated already reflected the id). Skip the state work
    // — remounting ChatView would reload messages and drop any in-flight ones —
    // but still strip the one-shot params below.
    const alreadyActive = deepLinkChatId === activeChatIdRef.current;
    if (!alreadyActive) {
      setActiveChatId(deepLinkChatId);
      setActiveProjectChatId(null);
      setActiveProjectChatName(null);
      setPendingToolOverrides(null);
      setPendingMyFilesConfig(null);
      setPendingMyFilesLabel(null);
      setPendingModelOverride(null);
      // Search results and bookmarks open saved chats, not new sessions.
      setIsNewChatSession(false);
      setChatSessionKey((k) => k + 1);
      setActiveNav("new-chat");
      const chatOwner = searchParams.get("chatOwner");
      setDeepLinkChatOwnerId(chatOwner);
      const promptText = searchParams.get("prompt");
      if (promptText) {
        setPendingPrompt({ key: Date.now(), content: promptText, name: "", autoSend: true });
      }
    }
    // Keep ?chat in the URL so a refresh/bookmark restores this chat; only the
    // one-shot params (prompt, chatOwner) are stripped.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("prompt");
    params.delete("chatOwner");
    const next = params.toString() ? `?${params.toString()}` : "/";
    router.replace(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkChatId]);

  // Project deep-link: ?project=<id>&prompt=<text> — from the project page.
  // No pre-created chat; let ChatView create it on first send with project_id set.
  const deepProjectId = searchParams.get("project");
  useEffect(() => {
    if (!deepProjectId) return;
    // When ?projectChat is also present, `project` is just context for restoring
    // that chat (handled by the effect below) — not a standalone project-prompt
    // deep link. Skip so we don't double-bump the session key or strip the param.
    if (searchParams.get("projectChat")) return;
    const promptText = searchParams.get("prompt");
    const filesParam = searchParams.get("files");
    const liveStartRequested = searchParams.get("live") === "1";
    if (promptText || filesParam || liveStartRequested) {
      // A project launch always starts a fresh conversation, including when
      // Home retains a previously selected chat or temporary-chat state.
      setActiveChatId(null);
      setActiveProjectChatId(null);
      setActiveProjectChatName(null);
      setDeepLinkChatOwnerId(null);
      setIncognitoMode(false);
      setIsNewChatSession(true);
      setPendingPrompt(null);
      setPendingMyFilesConfig(null);
      setPendingMyFilesLabel(null);
      if (promptText || filesParam) {
        let files: FileUIPart[] | undefined;
        if (filesParam) {
          try {
            const parsed = JSON.parse(filesParam) as Array<{
              url: string;
              filename?: string;
              mediaType: string;
            }>;
            files = parsed.map((f) => ({ ...f, type: "file" as const }));
          } catch {
            files = undefined;
          }
        }
        setPendingPrompt({ key: Date.now(), content: promptText ?? "", name: "", autoSend: true, files });
      }
      setPendingProjectId(deepProjectId);
      setPendingLiveChatStart(liveStartRequested);
      const ws = searchParams.get("ws");
      const ce = searchParams.get("ce");
      setPendingToolOverrides(
        ws !== null || ce !== null
          ? {
              ...(ws !== null ? { webSearch: ws === "1" } : {}),
              ...(ce !== null ? { codeExecution: ce === "1" } : {}),
            }
          : null
      );
      const modelParam = searchParams.get("model");
      setPendingModelOverride(isValidModelId(modelParam) ? modelParam : null);
    }
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    const params = new URLSearchParams(searchParams.toString());
    params.delete("project");
    params.delete("prompt");
    params.delete("files");
    params.delete("ws");
    params.delete("ce");
    params.delete("model");
    params.delete("live");
    const next = params.toString() ? `?${params.toString()}` : "/";
    router.replace(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepProjectId]);

  // Knowledge Base deep-link: ?kbFileIds=<id,id>&kbCollectionIds=<id,id> — from
  // the Knowledge Base page's "Start chat" action. Starts a fresh, non-project
  // chat with the myFiles tool forced on and scoped to just those files/
  // collections (expanded and re-validated server-side against the caller's
  // own completed files, so arbitrary ids here can't leak another user's data).
  const deepKbFileIds = searchParams.get("kbFileIds");
  const deepKbCollectionIds = searchParams.get("kbCollectionIds");
  const deepKbLabel = searchParams.get("kbLabel");
  useEffect(() => {
    if (!deepKbFileIds && !deepKbCollectionIds) return;
    const fileIds = deepKbFileIds ? deepKbFileIds.split(",").filter(Boolean) : [];
    const collectionIds = deepKbCollectionIds ? deepKbCollectionIds.split(",").filter(Boolean) : [];
    setPendingMyFilesConfig({
      fileIds,
      collectionIds,
      ...(deepKbLabel ? { label: deepKbLabel } : {}),
    });
    setPendingMyFilesLabel(deepKbLabel);
    setPendingToolOverrides({ myFiles: true });
    setPendingPrompt(null);
    setPendingProjectId(null);
    setPendingModelOverride(null);
    setActiveChatId(null);
    setActiveProjectChatId(null);
    setActiveProjectChatName(null);
    setIsNewChatSession(true);
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    const params = new URLSearchParams(searchParams.toString());
    params.delete("kbFileIds");
    params.delete("kbCollectionIds");
    params.delete("kbLabel");
    const next = params.toString() ? `?${params.toString()}` : "/";
    router.replace(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepKbFileIds, deepKbCollectionIds, deepKbLabel]);

  // Project chat deep-link: ?projectChat=<id>&project=<pid>&chatName=<name>
  // Opens an existing project_chats record directly in ChatView.
  const deepProjectChatId = searchParams.get("projectChat");
  useEffect(() => {
    if (!deepProjectChatId) return;
    // Already the active project chat (handleProjectChatCreated already reflected
    // it, or we just navigated here). Skip the state work so ChatView is not
    // remounted and in-flight messages are kept; only strip the one-shot prompt.
    const alreadyActive = deepProjectChatId === activeProjectChatIdRef.current;
    if (!alreadyActive) {
      setActiveProjectChatId(deepProjectChatId);
      setActiveProjectChatName(searchParams.get("chatName"));
      setActiveChatId(null);
      const pid = searchParams.get("project");
      if (pid) setPendingProjectId(pid);
      setPendingToolOverrides(null);
      setPendingMyFilesConfig(null);
      setPendingMyFilesLabel(null);
      setPendingModelOverride(null);
      setIsNewChatSession(false);
      setChatSessionKey((k) => k + 1);
      setActiveNav("new-chat");
    }
    // Keep projectChat/project/chatName in the URL so a refresh/bookmark restores
    // this project chat; only the one-shot prompt is stripped.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("prompt");
    const next = params.toString() ? `?${params.toString()}` : "/";
    router.replace(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepProjectChatId]);

  // Load the persisted model for the active project chat. Personal chats read
  // theirs from the `chats` list; project chats need a direct lookup since
  // only the id/name travel via the deep-link URL.
  useEffect(() => {
    if (!activeProjectChatId) {
      setActiveProjectChatModel(null);
      setActiveProjectChatOwnerId(null);
      setActiveProjectChatAgentId(null);
      return;
    }
    setActiveProjectChatOwnerId(null);
    let cancelled = false;
    void supabase
      .from("project_chats")
      .select("model, user_id, agent_id")
      .eq("id", activeProjectChatId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setActiveProjectChatModel(data?.model || null);
        setActiveProjectChatOwnerId(data?.user_id || null);
        setActiveProjectChatAgentId(data?.agent_id || null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProjectChatId]);

  // Standalone prompt deep-link: ?promptContent=<text>&promptName=<name> — from
  // the prompt library page. Inserts into a new chat's input without sending,
  // unlike the ?prompt= param used alongside ?chat=/?project= (which auto-sends).
  const deepLinkPromptContent = searchParams.get("promptContent");
  useEffect(() => {
    if (!deepLinkPromptContent) return;
    setPendingPrompt({
      key: Date.now(),
      content: deepLinkPromptContent,
      name: searchParams.get("promptName") ?? "",
    });
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    const params = new URLSearchParams(searchParams.toString());
    params.delete("promptContent");
    params.delete("promptName");
    const next = params.toString() ? `?${params.toString()}` : "/";
    router.replace(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPromptContent]);

  // Chats are scoped to the user's personal team.
  const activeTeamId = personalTeamId;
  const activeTeamName = useMemo(
    () =>
      teams.find((team) => team.id === activeTeamId)?.name ?? null,
    [activeTeamId, teams]
  );
  // Pre-bind the resolved team so createChat never needs an extra DB lookup
  const boundCreateChat = useMemo(
    () =>
      (params: { name: string; model: string; agentId?: string; projectId?: string; type?: DbChatType; chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null }) =>
        createChat({ ...params, teamId: activeTeamId ?? undefined }),
    [createChat, activeTeamId]
  );

  // createProjectChat resolves workspace_id internally via getPersonalTeamId()
  const boundCreateProjectChat = useMemo(
    () =>
      (params: { name: string; model: string; agentId?: string; projectId: string; chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null }) =>
        createProjectChat(params),
    [createProjectChat]
  );
  const displayedChatId = incognitoMode ? null : activeChatId;
  // Hide the temporary-chat toggle as soon as a conversation exists — general
  // chat (activeChatId) or project chat (activeProjectChatId). It only stays
  // visible in incognito mode so the user can turn it back off.
  const showIncognitoToggle =
    incognitoMode || (activeChatId === null && activeProjectChatId === null);
const showSystem = activeNav === "system";
  const showAdminChats = activeNav === "chats";
  const showMyFeedback = activeNav === "my-feedback";
  const showFiles = activeNav === "files";
  const showAnalytics = activeNav === "analytics";
  const showAgentAnalytics = activeNav === "agent-analytics";
  const showModelAnalytics = activeNav === "model-analytics";
  const showUsers = activeNav === "users";
  const showParseJobs = activeNav === "parse-jobs";
  const showMailComms = activeNav === "mail-comms";
  const showEvaluations = activeNav === "evaluations";
  const showTokenAnalytics = activeNav === "token-analytics";
  const showArchivedProjects = activeNav === "archived-projects";
  const showArchivedAgents = activeNav === "archived-agents";
  const showMemories = activeNav === "memories";
  const showDocumentation = activeNav === "documentation";
  const showPolicy = activeNav === "policy";
  const showPlayground = activeNav === "playground";
  useTabTitle(
    session === null ? "Sign in" : profileOpen ? "Profile" : HOME_PANEL_TITLES[activeNav]
  );
  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === activeChatId) ?? null,
    [activeChatId, chats]
  );
  // Parse per-chat tool settings for the currently selected chat.
  // null when no chat is selected or the chat pre-dates this feature.
  const activeChatTools = useMemo(() => {
    if (!activeChat?.chat_tools) return null;
    return profileToolsToRecord(activeChat.chat_tools);
  }, [activeChat]);
  const activeChatMyFilesConfig = useMemo<MyFilesScopeConfig | null>(() => {
    const rawConfig = activeChat?.chat_tools?.find(
      (entry) => entry.tool_id === BUILTIN_TOOL_UUID.myFiles
    )?.config;
    if (!rawConfig) return null;

    const fileIds = Array.isArray(rawConfig.fileIds)
      ? rawConfig.fileIds.filter((value): value is string => typeof value === "string" && value.length > 0)
      : [];
    const collectionIds = Array.isArray(rawConfig.collectionIds)
      ? rawConfig.collectionIds.filter((value): value is string => typeof value === "string" && value.length > 0)
      : [];
    if (fileIds.length === 0 && collectionIds.length === 0) return null;

    return {
      fileIds,
      collectionIds,
      ...(typeof rawConfig.label === "string" && rawConfig.label.length > 0
        ? { label: rawConfig.label }
        : {}),
    };
  }, [activeChat]);
  const resolvedActiveAgent = useMemo<DbAgent | null>(() => {
    const agentId = searchParams.get("agent");
    if (agentId) {
      return agents.find((agent) => agent.id === agentId) ?? null;
    }
    // Project chats aren't in the `chats` list, so their persisted agent_id
    // comes from a direct fetch (activeProjectChatAgentId) instead of
    // activeChat.agent_id.
    const persistedAgentId = activeProjectChatId
      ? activeProjectChatAgentId
      : activeChat?.agent_id;
    if (!persistedAgentId) {
      return null;
    }
    return agents.find((agent) => agent.id === persistedAgentId) ?? null;
  }, [activeChat, activeProjectChatId, activeProjectChatAgentId, agents, searchParams]);
  const resolvedAgentLabel =
    resolvedActiveAgent?.name ??
    activeChat?.agent_name ??
    undefined;

  // Reflect the active chat id in the address bar WITHOUT a Next navigation, so
  // useSearchParams() is unchanged and the ?chat deep-link effect does not
  // re-fire (which would remount ChatView and wipe in-flight messages).
  // Enables bookmarking and refresh-to-restore.
  const writeChatIdToUrl = useCallback((chatId: string | null) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (chatId) params.set("chat", chatId);
    else params.delete("chat");
    // a personal chat and a project chat are mutually exclusive in the bar
    params.delete("projectChat");
    params.delete("project");
    params.delete("chatName");
    // one-shot params never persist in the bar
    params.delete("prompt");
    params.delete("chatOwner");
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `/?${qs}` : "/");
  }, []);

  // Project-chat equivalent of writeChatIdToUrl. Restores via the ?projectChat
  // deep-link effect on refresh; project id and name mirror how the project page
  // links to a chat, so no extra DB fetch is needed to show the title.
  const writeProjectChatIdToUrl = useCallback(
    (id: string, projectId: string, name: string, agentId?: string) => {
      if (typeof window === "undefined") return;
      const params = new URLSearchParams(window.location.search);
      params.set("projectChat", id);
      params.set("project", projectId);
      if (name) params.set("chatName", name);
      else params.delete("chatName");
      if (agentId) params.set("agent", agentId);
      params.delete("chat");
      params.delete("prompt");
      params.delete("chatOwner");
      const qs = params.toString();
      window.history.replaceState(null, "", qs ? `/?${qs}` : "/");
    },
    []
  );

  const handleNewChat = useCallback(() => {
    setPendingLiveChatStart(false);
    if (!incognitoMode) {
      setActiveChatId(null);
    }
    setActiveProjectChatId(null);
    setActiveProjectChatName(null);
    setPendingProjectId(null);
    setPendingToolOverrides(null);
    setPendingMyFilesConfig(null);
    setPendingMyFilesLabel(null);
    setPendingModelOverride(null);
    setIsNewChatSession(true);
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    router.replace("/");
  }, [incognitoMode, router]);

  // Open the chat a feedback item was submitted on (My Feedback panel).
  // Skips the chats-list membership guard: the sidebar list only holds the
  // 50 most recent chats while feedback can reference older ones. ChatView
  // loads the chat and its messages by id through RLS-scoped queries, so
  // only the signed-in user's own (or shared) chats can ever resolve.
  const handleOpenFeedbackChat = useCallback((id: string) => {
    setIncognitoMode(false);
    setActiveChatId(id);
    setActiveProjectChatId(null);
    setActiveProjectChatName(null);
    setPendingProjectId(null);
    setPendingToolOverrides(null);
    setPendingMyFilesConfig(null);
    setPendingMyFilesLabel(null);
    setPendingModelOverride(null);
    setIsNewChatSession(false);
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    // Reflect the chat id so it can be bookmarked / restored on refresh; the
    // deep-link effect's alreadyActive guard prevents a redundant remount.
    router.replace(`/?chat=${id}`);
  }, [router]);

  // After "Remove from project": the old project chat is gone (deleted server
  // side) and its messages now live under a new personal chat id. A full
  // remount (chatSessionKey bump) is required here, unlike handleChatCreated's
  // in-place ID swap, because there's no in-flight stream to preserve — we
  // want ChatView to drop its stale project-chat state and load the new
  // chats/messages rows fresh.
  const handleChatMovedToPersonal = useCallback((id: string) => {
    setIncognitoMode(false);
    setActiveChatId(id);
    setActiveProjectChatId(null);
    setActiveProjectChatName(null);
    setPendingProjectId(null);
    setPendingToolOverrides(null);
    setPendingMyFilesConfig(null);
    setPendingMyFilesLabel(null);
    setPendingModelOverride(null);
    setIsNewChatSession(false);
    setChatSessionKey((k) => k + 1);
    setActiveNav("new-chat");
    router.replace(`/?chat=${id}`);
  }, [router]);

  // The hidden move action physically replaces the personal chat with a
  // project_chat. Switch identities immediately after its final turn has
  // been persisted so the next message cannot target the deleted chats row.
  const handleChatMovedToProject = useCallback(({
    projectChatId,
    projectId,
    name,
  }: {
    projectChatId: string;
    projectId: string;
    name: string;
  }) => {
    setIncognitoMode(false);
    setActiveChatId(null);
    setActiveProjectChatId(projectChatId);
    setActiveProjectChatName(name);
    setPendingProjectId(projectId);
    setPendingToolOverrides(null);
    setPendingMyFilesConfig(null);
    setPendingMyFilesLabel(null);
    setPendingModelOverride(null);
    setIsNewChatSession(false);
    setChatSessionKey((key) => key + 1);
    setActiveNav("new-chat");
    refresh();
    router.replace(
      `/?projectChat=${projectChatId}&project=${projectId}&chatName=${encodeURIComponent(name)}`
    );
  }, [refresh, router]);

  // Project chats have no reactive local list the way personal chats do
  // (updateChatName updates the `chats` array itself), so a successful
  // rename needs to explicitly update the display-name state here too.
  const handleProjectChatRenamed = useCallback((name: string) => {
    setActiveProjectChatName(name);
    if (activeProjectChatId && pendingProjectId) {
      writeProjectChatIdToUrl(activeProjectChatId, pendingProjectId, name);
    }
  }, [activeProjectChatId, pendingProjectId, writeProjectChatIdToUrl]);


  const handleEditAgent = useCallback(
    (agentId: string) => {
      setAgentDialogOpen(false);
      router.push(`/agents/${agentId}/edit`);
    },
    [router]
  );

  const handleCreateAgent = useCallback(() => {
    setAgentDialogOpen(false);
    router.push("/agents/new/edit");
  }, [router]);

  const handleSelectAgent = useCallback(
    async ({ agent }: AgentDialogSelection) => {
      setAgentDialogOpen(false);

      // Switching an existing conversation changes its persistent active
      // agent in place. Previous messages keep their own agent attribution;
      // subsequent turns use the newly selected agent.
      if (activeProjectChatId) {
        const { data, error } = await supabase
          .from("project_chats")
          .update({ agent_id: agent.id, model: agent.model })
          .eq("id", activeProjectChatId)
          .select("id")
          .maybeSingle();
        if (error || !data) {
          console.error("[Home] update project chat agent:", error ?? "No chat updated");
          toast.error("Failed to switch agents.");
          return;
        }
        setActiveProjectChatAgentId(agent.id);
        setActiveProjectChatModel(agent.model);
        if (pendingProjectId) {
          writeProjectChatIdToUrl(
            activeProjectChatId,
            pendingProjectId,
            activeProjectChatName ?? "",
            agent.id
          );
        } else {
          router.replace(`/?projectChat=${activeProjectChatId}&agent=${agent.id}`);
        }
        toast.success(`Switched to ${agent.name}.`);
        return;
      }

      if (incognitoMode) {
        router.replace(`/?agent=${agent.id}`);
        setIsNewChatSession(true);
        setChatSessionKey((k) => k + 1);
        setActiveNav("new-chat");
        return;
      }

      if (activeChatId) {
        const updated = await updateChatAgent(
          activeChatId,
          agent.id,
          agent.name,
          agent.model
        );
        if (!updated) {
          toast.error("Failed to switch agents.");
          return;
        }
        router.replace(`/?chat=${activeChatId}&agent=${agent.id}`);
        toast.success(`Switched to ${agent.name}.`);
        return;
      }

      // A project context without a persisted chat remains unsaved until its
      // first message, but the chosen agent is retained for that creation.
      if (pendingProjectId) {
        router.replace(`/?agent=${agent.id}`);
        return;
      }

      const newId = await createChat({
        name: agent.name,
        model: agent.model,
        agentId: agent.id,
        teamId: activeTeamId ?? undefined,
      });

      if (!newId) {
        return;
      }

      setActiveChatId(newId);
      setIsNewChatSession(true);
      setChatSessionKey((k) => k + 1);
      setActiveNav("new-chat");
      router.replace(`/?chat=${newId}&agent=${agent.id}`);
    },
    [activeChatId, activeProjectChatId, activeProjectChatName, activeTeamId, createChat, incognitoMode, pendingProjectId, router, updateChatAgent, writeProjectChatIdToUrl]
  );

  const handleUseAgent = useCallback(
    (agentId: string) => {
      const agent = agents.find((candidate) => candidate.id === agentId);
      if (!agent) {
        toast.error("That agent is no longer available.");
        return;
      }
      void handleSelectAgent({
        agent,
        teamId: agent.teams[0]?.id ?? null,
      });
    },
    [agents, handleSelectAgent]
  );

  const handleAgentDeactivated = useCallback(() => {
    if (activeProjectChatId) {
      setActiveProjectChatAgentId(null);
    } else if (activeChatId) {
      applyChatAgentDeactivation(activeChatId);
    }

    const params = new URLSearchParams(window.location.search);
    params.delete("agent");
    const query = params.toString();
    router.replace(query ? `/?${query}` : "/");
  }, [activeChatId, activeProjectChatId, applyChatAgentDeactivation, router]);

  const handleChatCreated = useCallback(
    (id: string) => {
      // Only update the ID — do NOT change chatSessionKey, which would remount
      // ChatView and erase the in-flight messages.
      setActiveChatId(id);
      setActiveProjectChatId(null);
      setActiveProjectChatName(null);
      setPendingProjectId(null);
      // Reflect the new chat id in the URL (bookmark / refresh-to-restore).
      // Uses history.replaceState — no navigation, so ChatView is not remounted.
      writeChatIdToUrl(id);
      refresh();
    },
    [refresh, writeChatIdToUrl]
  );

  const handleProjectChatCreated = useCallback(
    ({ id, projectId, name }: { id: string; projectId: string; name: string }) => {
      // Mirror handleChatCreated for project chats: adopt the id into state and
      // reflect it in the URL WITHOUT bumping chatSessionKey (no remount).
      setActiveProjectChatId(id);
      setActiveProjectChatName(name);
      setActiveChatId(null);
      setPendingProjectId(projectId);
      writeProjectChatIdToUrl(id, projectId, name);
      refresh();
    },
    [refresh, writeProjectChatIdToUrl]
  );

  const handleSaveHtmlToArtifacts = useCallback(
    async (name: string, url: string): Promise<{ ok: boolean }> => {
      // Prefer the already-resolved team; fall back to a direct DB lookup in case
      // useTeams() hasn't finished loading yet when the user clicks Save.
      let teamId = activeTeamId;
      if (!teamId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: homeTeam } = await supabase
            .from("teams")
            .select("id")
            .eq("user_id", user.id)
            .eq("is_home", true)
            .maybeSingle();
          teamId = (homeTeam as { id: string } | null)?.id ?? null;
        }
      }
      if (!teamId) {
        toast.error("No team found. Cannot save artifact.");
        return { ok: false };
      }
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch HTML content.");
        const htmlContent = await response.text();
        const id = await createArtifact({
          name,
          htmlContent,
          teamId,
          sourceChatId: activeChatId,
          // Tags the artifact with the project it was created in, independent of
          // the project_resources link below (which controls visibility in the
          // project's Artifacts section). Used solely to scope project-registers
          // access, so the artifact keeps register access even if that link fails
          // or is later removed.
          originProjectId: pendingProjectId,
        });
        if (id) {
          // Artifacts saved from inside a project chat belong to that project,
          // not the user's personal artifacts list — link them via
          // project_resources so they surface in the project's
          // Artifacts panel (and are filtered out of the personal list).
          let linkedToProject = false;
          if (pendingProjectId) {
            const { error: linkError } = await supabase
              .from("project_resources")
              .insert({
                project_id: pendingProjectId,
                workspace_id: teamId,
                resource_type: "artifact",
                resource_id: id,
              });
            // 23505 = unique_violation: the artifact is already linked to the
            // project, so treat it as success rather than warning the user.
            if (linkError && linkError.code !== "23505") {
              console.error("[Home] link artifact to project:", linkError);
            } else {
              linkedToProject = true;
            }
          }
          if (pendingProjectId && !linkedToProject) {
            toast.warning("Saved to Artifacts, but couldn't add it to the project.", {
              action: { label: "View", onClick: () => router.push(`/artifacts/${id}`) },
            });
            return { ok: false };
          }
          const viewHref = linkedToProject
            ? `/artifacts/${id}?projectId=${pendingProjectId}`
            : `/artifacts/${id}`;
          toast.success(linkedToProject ? "Added to project artifacts." : "Saved to Artifacts.", {
            action: { label: "View", onClick: () => router.push(viewHref) },
          });
          return { ok: true };
        }
        toast.error("Failed to save artifact.");
        return { ok: false };
      } catch (err) {
        console.error("[Home] save artifact:", err);
        toast.error("Failed to save artifact.");
        return { ok: false };
      }
    },
    [activeTeamId, activeChatId, pendingProjectId, createArtifact, router]
  );

  const handleEditTool = useCallback(
    (tool: { id: string }) => {
      setToolsDialogOpen(false);
      router.push(`/tools/${tool.id}/edit`);
    },
    [router]
  );

  const handleCreateTool = useCallback(() => {
    setToolsDialogOpen(false);
    router.push("/tools/new/edit");
  }, [router]);

  // Panels rendered as in-page activeNav state (Users, Analytics, etc.) have
  // no route of their own, so without this a refresh always lands back on
  // new-chat — keep the URL in sync so `nav` can rehydrate the panel on load.
  const goHome = useCallback(() => {
    setActiveNav("new-chat");
    const returnTo = typeof window === "undefined"
      ? null
      : readSafeReturnTo(window.location.search);
    router.push(returnTo ?? "/");
  }, [router]);

  const handleNavClick = useCallback(
    (item: SidebarItem) => {
      setProfileOpen(false);
      setMobileMenuOpen(false);
      const route = SIDEBAR_ROUTES[item];
      if (route) {
        router.push(route);
        return;
      }
      if (item === "tools") {
        setToolsDialogOpen(true);
        return;
      }
      if (item === "documentation") {
        setDocumentationDocId(null);
      }
      setActiveNav(item);
      if (item === "new-chat") {
        handleNewChat();
        router.replace("/");
      } else {
        router.replace(`/?nav=${item}`);
      }
    },
    [handleNewChat, router]
  );

  const navDeepLink = searchParams.get("nav") as SidebarItem | null;
  const docDeepLink = searchParams.get("doc");

  useEffect(() => {
    const nav = navDeepLink;
    const doc = docDeepLink;
    if (nav) {
      setProfileOpen(false);
      setMobileMenuOpen(false);
      if (nav === "documentation") {
        setDocumentationDocId(doc);
      } else if (nav === "new-chat") {
        handleNewChat();
        return;
      }
      setActiveNav(nav);
    }
  }, [docDeepLink, handleNewChat, navDeepLink]);

  const handleToggleIncognitoMode = useCallback(() => {
    setIncognitoMode((current) => !current);
    setActiveNav("new-chat");
    setIsNewChatSession(true);
    setChatSessionKey((k) => k + 1);
    router.replace("/");
  }, [router]);

  if (session === undefined) return <ChatSkeleton />;
  if (session === null) return <SignInScreen />;

  const sharedSidebarProps = {
    activeItem: activeNav,
    onItemClick: handleNavClick,
    userRole,
    user: { name: userDisplayName || "Profile", initials: userDisplayName ? userInitials : "U" },
    activeTeamId,
    teams,
    sessionUserId,
    workflowsEnabled,
    codeSpacesEnabled,
  };

  return (
    <div className="fixed inset-0 flex overflow-hidden overscroll-none bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <AppSidebar
          {...sharedSidebarProps}
          onProfileClick={() => setProfileOpen(true)}
        />
      </div>

      {/* Mobile Header with Menu Button */}
      <div className="md:hidden absolute top-0 left-0 z-50 p-2">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon size={24} />
          </Button>
          <SheetContent side="left" className="w-48 p-0">
            <AppSidebar
              {...sharedSidebarProps}
              onProfileClick={() => {
                setProfileOpen(true);
                setMobileMenuOpen(false);
              }}
              onNotificationsNavigate={() => setMobileMenuOpen(false)}
              listMode
            />
          </SheetContent>
        </Sheet>
      </div>

      {profileOpen ? (
        <ProfilePanel
          session={session}
          onBack={() => setProfileOpen(false)}
          onOpenDocumentation={(docId) => {
            setDocumentationDocId(docId);
            setProfileOpen(false);
            setActiveNav("documentation");
          }}
        />
      ) : (
        <>
          <AgentDialog
            open={agentDialogOpen}
            onOpenChange={setAgentDialogOpen}
            agents={agents}
            teams={teams}
            currentUserId={sessionUserId}
            onSelect={handleSelectAgent}
            onCreate={handleCreateAgent}
            onEdit={(agent) => handleEditAgent(agent.id)}
            onLearn={() => { setDocumentationDocId("agents"); setActiveNav("documentation"); }}
          />

          <ToolsBrowseDialog
            open={toolsDialogOpen}
            onOpenChange={setToolsDialogOpen}
            tools={tools}
            onEdit={handleEditTool}
            onCreateTool={handleCreateTool}
            isSuperAdmin={userRole === "superadmin"}
          />

          {showPlayground ? (
            <PlaygroundPanel onBack={goHome} />
          ) : showPolicy ? (
            <PolicyPanel onBack={goHome} />
          ) : showDocumentation ? (
            <DocumentationPanel
              key={documentationDocId ?? "documentation"}
              initialDocId={documentationDocId}
              onBack={goHome}
              userRole={userRole}
            />
          ) : showSystem ? (
            <SystemPanel onBack={goHome} />
          ) : showAdminChats ? (
            <AdminChatsPanel onBack={goHome} />
          ) : showMyFeedback ? (
            <MyFeedbackPanel
              onBack={goHome}
              onOpenChat={handleOpenFeedbackChat}
            />
          ) : showFiles ? (
            <FilesPanel
              teams={teams}
              teamsLoading={teamsLoading}
              activeTeamId={activeTeamId}
              currentUserId={sessionUserId}
              userRole={userRole}
              onBack={goHome}
              onLearn={() => { setDocumentationDocId("knowledge-base"); setActiveNav("documentation"); }}
            />
          ) : showAnalytics ? (
            <AnalyticsPanel
              onBack={goHome}
              canViewTopTokenUsers={userRole === "admin" || userRole === "superadmin"}
              canViewManagedAgents={userRole === "superadmin"}
            />
          ) : showAgentAnalytics ? (
            <AgentAnalyticsPanel onBack={goHome} />
          ) : showModelAnalytics ? (
            <ModelAnalyticsPanel onBack={goHome} />
          ) : showUsers ? (
            <UsersPanel onBack={goHome} userRole={userRole} />
          ) : showParseJobs ? (
            <ParseJobsPanel onBack={goHome} />
          ) : showMailComms ? (
            <MailCommsPanel onBack={goHome} />
          ) : showEvaluations && userRole === "superadmin" ? (
            <EvaluationsPanel onBack={goHome} />
          ) : showArchivedProjects ? (
            <ArchivedProjectsPanel onBack={goHome} />
          ) : showArchivedAgents ? (
            <ArchivedAgentsPanel onBack={goHome} />
          ) : showTokenAnalytics ? (
            <TokenAnalyticsPanel onBack={goHome} />
          ) : showMemories ? (
            <MemoriesPanel
              currentUserId={sessionUserId}
              onBack={goHome}
              onLearn={() => { setDocumentationDocId("memories"); setActiveNav("documentation"); }}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <ChatView
                  key={chatSessionKey}
                  userId={sessionUserId}
                  isSuperAdmin={userRole === "superadmin"}
                  agents={agents}
                  activeAgent={resolvedActiveAgent}
                  pendingAgentId={searchParams.get("agent")}
                  agentsLoading={agentsLoading}
                  activeAgentLabel={resolvedAgentLabel}
                  activeTeamName={activeTeamName}
                  chatName={activeProjectChatId ? activeProjectChatName : (activeChat?.name ?? null)}
                  chatModel={activeProjectChatId ? activeProjectChatModel : (activeChat?.model ?? null)}
                  projectId={pendingProjectId ?? null}
                  chatSharing={activeChat?.sharing ?? null}
                  chatOwnerId={
                    activeProjectChatId
                      // Never fall back to null (which chat-view.tsx reads as
                      // "unowned, treat me as owner") while the owner fetch is
                      // in flight — that reopens the shared-chat send bug.
                      ? (activeProjectChatOwnerId ?? PROJECT_CHAT_OWNER_LOADING)
                      : (activeChat?.user_id ?? deepLinkChatOwnerId ?? null)
                  }
                  isUserNameReady={isProfileLoaded}
                  userName={isProfileLoaded ? userFirstName : ""}
                  chatId={displayedChatId}
                  projectChatId={activeProjectChatId}
                  isNewSession={isNewChatSession}
                  incognito={incognitoMode}
                  onChatCreated={handleChatCreated}
                  onProjectChatCreated={handleProjectChatCreated}
                  onActivateAgent={() => setAgentDialogOpen(true)}
                  onUseAgent={handleUseAgent}
                  onAgentDeactivated={handleAgentDeactivated}
                  onChatMovedToProject={handleChatMovedToProject}
                  pendingPrompt={pendingPrompt}
                  startLiveChat={pendingLiveChatStart}
                  onLiveChatStarted={() => setPendingLiveChatStart(false)}
                  onPromptConsumed={() => setPendingPrompt(null)}
                  titleOverride={resolvedActiveAgent?.name}
                  initialChatTools={activeChatTools}
                  initialToolOverrides={pendingToolOverrides}
                  initialMyFilesConfig={pendingMyFilesConfig ?? activeChatMyFilesConfig}
                  initialMyFilesLabel={pendingMyFilesLabel ?? activeChatMyFilesConfig?.label ?? null}
                  initialModelOverride={pendingModelOverride}
                  createChat={boundCreateChat}
                  createProjectChat={boundCreateProjectChat}
                  updateChatName={updateChatName}
                  updateChatModel={updateChatModel}
                  updateProjectChatModel={updateProjectChatModel}
                  updateProjectChatName={updateProjectChatName}
                  deleteChat={deleteChat}
                  deleteProjectChat={deleteProjectChat}
                  onChatDeleted={handleNewChat}
                  onChatMovedToPersonal={handleChatMovedToPersonal}
                  onProjectChatRenamed={handleProjectChatRenamed}
                  onProjectChanged={refresh}
                  onSaveHtmlToArtifacts={handleSaveHtmlToArtifacts}
                  showIncognitoToggle={showIncognitoToggle}
                  onToggleIncognito={handleToggleIncognitoMode}
                />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function HomeClient(props: HomeClientProps) {
  return (
    <Suspense fallback={null}>
      <HomeContent {...props} />
    </Suspense>
  );
}
